import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
  BarChart3,
  Bell,
  BriefcaseBusiness,
  Building2,
  Camera,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  FileText,
  LayoutDashboard,
  Save,
  Settings2,
  ShieldCheck,
  UserCog,
  UserRound,
  Users,
  UserPlus,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { buildAppPath } from '@/utils/tenant';
import { canAccessSection, SECTIONS } from '@/utils/accessControl';
import { getRoleMetadata, normalizeRole, ROLES } from '@/utils/roles';
import tokenManager from '@/services/auth/tokenManager';
import RolesAccessPanel from './RolesAccessPanel';
import { setUser } from 'redux/slices/authSlice';
import { useUploadUserProfilePhotoMutation } from 'redux/services/admin/userManagementV2';
import { useUploadConsumerProfilePhotoMutation } from 'redux/services/client/onboarding';
import {
  applicationAPI,
  useGetBorrowerScopedProfileQuery,
  useGetProfileBootstrapQuery,
  useUpdateBorrowerScopedProfileMutation,
} from 'redux/services/client/profile';
import { useGetPipelineQuery } from 'redux/services/client/pipeline';
import { createReactSelectStyles } from '@/utils/reactSelectStyles';
import styles from './AccountQuickPanel.module.css';

const toTitleCase = (value = '') =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const getInitials = (name = '') =>
  String(name)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';

const getUserId = (user = {}) =>
  user?.user_id || user?.userId || user?.id || null;

const getPhotoUrlFromResponse = (response = {}) =>
  response?.data?.profile_picture_url ||
  response?.profile_picture_url ||
  response?.data?.user?.profile_picture_url ||
  response?.user?.profile_picture_url ||
  response?.data?.profilePictureUrl ||
  response?.profilePictureUrl ||
  '';

const normalizeErrorText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(normalizeErrorText).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const directMessage =
      value?.detail || value?.message || value?.error || value?.title || value?.reason;
    if (directMessage) return normalizeErrorText(directMessage);

    const fieldMessages = Object.entries(value)
      .map(([field, fieldValue]) => {
        const text = normalizeErrorText(fieldValue);
        return text ? `${field}: ${text}` : '';
      })
      .filter(Boolean);

    if (fieldMessages.length) return fieldMessages.join(' | ');
  }

  return '';
};

const getReadableErrorMessage = (error, fallback) => {
  const candidates = [
    error?.data?.detail,
    error?.data?.message,
    error?.data?.error,
    error?.error,
    error?.message,
    error?.data,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeErrorText(candidate);
    if (normalized) return normalized;
  }

  return fallback;
};

const normalizeApplicationId = (value) => {
  if (!value) return '';
  const normalized = String(value).trim();
  if (!normalized || normalized === 'null' || normalized === 'undefined') {
    return '';
  }
  return normalized;
};

const PRIMARY_B2B_ACTION_KEYS = ['dashboard', 'loan-info', 'pipeline', 'quick-price', 'tools'];
const ADMIN_B2B_ACTION_KEYS = ['reports', 'employees', 'consumers', 'branches', 'invitations'];

const getWorkspaceSummary = ({ pathname = '', search = '', tenant = '', branch = '', roleDescription = '' }) => {
  const params = new URLSearchParams(search || '');
  const applicationId = normalizeApplicationId(
    params.get('applicationId') || params.get('application_id') || ''
  );

  if (pathname.startsWith('/loanInformation/lead')) {
    return {
      eyebrow: 'Current file',
      title: applicationId ? `Application ${applicationId}` : 'Loan information file',
      description: 'You are inside a borrower file in Loan Information.',
      details: [tenant && `Organization: ${tenant}`, branch && `Branch: ${branch}`].filter(Boolean),
    };
  }

  if (pathname.startsWith('/pipeline/lead')) {
    return {
      eyebrow: 'Current file',
      title: applicationId ? `Application ${applicationId}` : 'Pipeline file',
      description: 'You are reviewing a borrower file from the Pipeline workspace.',
      details: [tenant && `Organization: ${tenant}`, branch && `Branch: ${branch}`].filter(Boolean),
    };
  }

  const workspaceMap = [
    {
      match: '/admin/employees',
      eyebrow: 'Workspace',
      title: 'Employee Administration',
      description: 'Manage employee access, roles, and branch assignments.',
    },
    {
      match: '/admin/consumers',
      eyebrow: 'Workspace',
      title: 'Consumer Administration',
      description: 'Review consumers, assignments, and onboarding progress.',
    },
    {
      match: '/admin/branches',
      eyebrow: 'Workspace',
      title: 'Branch Administration',
      description: 'Manage branch setup and organization structure.',
    },
    {
      match: '/admin/invitations',
      eyebrow: 'Workspace',
      title: 'Invitations',
      description: 'Track pending invites and resend access when needed.',
    },
    {
      match: '/loanInformation',
      eyebrow: 'Workspace',
      title: 'Loan Information',
      description: 'Prepare and review borrower file details before pipeline progression.',
    },
    {
      match: '/pipeline',
      eyebrow: 'Workspace',
      title: 'Pipeline',
      description: 'Monitor application progress and move files through stages.',
    },
    {
      match: '/quickPrice',
      eyebrow: 'Workspace',
      title: 'Quick Price',
      description: 'Run pricing scenarios and compare rate options quickly.',
    },
    {
      match: '/tools',
      eyebrow: 'Workspace',
      title: 'Tools',
      description: 'Use calculators and comparison tools for borrower scenarios.',
    },
    {
      match: '/reports',
      eyebrow: 'Workspace',
      title: 'Reports',
      description: 'Review production trends and export reporting data.',
    },
    {
      match: '/dashboard',
      eyebrow: 'Workspace',
      title: 'Dashboard',
      description: 'Track the current pipeline and operational activity at a glance.',
    },
  ];

  const match = workspaceMap.find((item) => pathname.startsWith(item.match));
  return {
    eyebrow: match?.eyebrow || 'Workspace',
    title: match?.title || 'B2B Workspace',
    description: match?.description || roleDescription || 'Review your workspace and quick actions.',
    details: [tenant && `Organization: ${tenant}`, branch && `Branch: ${branch}`].filter(Boolean),
  };
};

const getB2BActionItems = ({ normalizedRole, navigate, onNotifications, onSupport }) => {
  const actions = [];
  const pushAction = (key, label, icon, description, onClick, visible = true) => {
    if (!visible) return;
    actions.push({ key, label, icon, description, onClick });
  };

  pushAction(
    'dashboard',
    'Dashboard',
    LayoutDashboard,
    'Overview and current activity.',
    () => navigate(buildAppPath('/dashboard')),
    normalizedRole !== ROLES.LEAD_GEN
  );
  pushAction(
    'loan-info',
    'Loan Information',
    FileText,
    'Open borrower file details.',
    () => navigate(buildAppPath('/loanInformation')),
    canAccessSection(SECTIONS.LOAN_INFORMATION, normalizedRole)
  );
  pushAction(
    'pipeline',
    'Pipeline',
    ClipboardList,
    'Work active files and statuses.',
    () => navigate(buildAppPath('/pipeline')),
    canAccessSection(SECTIONS.PIPELINE, normalizedRole)
  );
  pushAction(
    'quick-price',
    'Quick Price',
    Zap,
    'Run scenario pricing.',
    () => navigate(buildAppPath('/quickPrice')),
    [ROLES.CLIENT_ADMIN, ROLES.BRANCH_ADMIN, ROLES.LOAN_OFFICER, ROLES.LOAN_PROCESSOR].includes(normalizedRole)
  );
  pushAction(
    'tools',
    'Tools',
    Wrench,
    'Compare offers and schedules.',
    () => navigate(buildAppPath('/tools')),
    [ROLES.CLIENT_ADMIN, ROLES.BRANCH_ADMIN, ROLES.LOAN_OFFICER, ROLES.LOAN_PROCESSOR].includes(normalizedRole)
  );
  pushAction(
    'reports',
    'Reports',
    BarChart3,
    'Review tenant performance.',
    () => navigate(buildAppPath('/reports')),
    [ROLES.CLIENT_ADMIN, ROLES.BRANCH_ADMIN].includes(normalizedRole)
  );
  pushAction(
    'employees',
    'Manage Employees',
    UserCog,
    'Roles, access, and branch assignment.',
    () => navigate(buildAppPath('/admin/employees')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  pushAction(
    'consumers',
    'Manage Consumers',
    Users,
    'View borrower roster and assignment.',
    () => navigate(buildAppPath('/admin/consumers')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  pushAction(
    'branches',
    'Manage Branches',
    Building2,
    'Review branch structure and settings.',
    () => navigate(buildAppPath('/admin/branches')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  pushAction(
    'invitations',
    'Invitations',
    UserPlus,
    'Manage access invites.',
    () => navigate(buildAppPath('/admin/invitations')),
    canAccessSection(SECTIONS.ADMIN, normalizedRole)
  );
  pushAction('notifications', 'Notifications', Bell, 'Review recent alerts.', onNotifications);
  pushAction('support', 'Help & Support', CircleHelp, 'Open support and resources.', onSupport);

  return actions;
};

const applicationSelectStyles = createReactSelectStyles({
  controlMinHeight: '44px',
  controlHeight: '44px',
  borderRadius: '12px',
  borderColor: '#cfd9ea',
  borderHoverColor: '#3b82f6',
  indicatorPadding: '0 6px',
});

const getAppContext = () => {
  if (typeof window === 'undefined') {
    return { applicationId: '', branch: '', tenant: '' };
  }

  const params = new URLSearchParams(window.location.search || '');
  const applicationIdFromUrl = params.get('applicationId') || params.get('application_id') || '';
  const applicationIdFromStorage = sessionStorage.getItem('b2c_active_application_id') || '';
  const applicationIdFromLocalStorage = localStorage.getItem('b2c_active_application_id') || '';

  return {
    applicationId: applicationIdFromUrl || applicationIdFromStorage || applicationIdFromLocalStorage,
    branch:
      sessionStorage.getItem('mira_branch_slug') ||
      sessionStorage.getItem('b2c_branch_id') ||
      '',
    tenant:
      sessionStorage.getItem('mira_client_slug') ||
      sessionStorage.getItem('b2c_organization_id') ||
      '',
  };
};

const AccountQuickPanel = ({
  user,
  mode = 'desktop',
  surface = 'popover',
  onClose,
  onNotifications,
  onSupport,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [localImageUrl, setLocalImageUrl] = useState('');
  const [showQuickEdit, setShowQuickEdit] = useState(false);
  // Swaps the right-hand pane from Work Areas to Roles & Access.
  const [showRoles, setShowRoles] = useState(false);
  const [quickForm, setQuickForm] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });

  useEffect(() => {
    return () => {
      if (localImageUrl) {
        URL.revokeObjectURL(localImageUrl);
      }
    };
  }, [localImageUrl]);

  const displayName = useMemo(() => {
    const fromNames = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    return user?.name || fromNames || user?.email || '';
  }, [user]);

  const normalizedRole = normalizeRole(user?.originalRole || user?.role || '');
  const roleMetadata = getRoleMetadata(normalizedRole);
  const role = roleMetadata?.label || toTitleCase(user?.originalRole || user?.role || '');
  const isConsumer = String(user?.originalRole || user?.role || '').toLowerCase() === 'consumer';
  const isMobileB2B = !isConsumer && mode === 'mobile';
  const isDesktopB2BModal = !isConsumer && mode !== 'mobile' && surface === 'modal';
  // Same gate as the Administration work areas. The server is what refuses the
  // write: only a client admin may save (`mira.settings.manage_rbac`).
  const canManageRoles = canAccessSection(SECTIONS.ADMIN, normalizedRole);
  const { applicationId, branch, tenant } = getAppContext();
  const normalizedApplicationId = normalizeApplicationId(applicationId);
  const { data: pipelineData } = useGetPipelineQuery(
    {},
    { skip: !isConsumer, refetchOnMountOrArgChange: 300 }
  );
  const applicationOptions = useMemo(() => {
    const applications = pipelineData?.data?.applications ?? [];
    return applications
      .map((app) => {
        const appId = normalizeApplicationId(app?.application_details?.applicationId);
        if (!appId) return null;
        const firstName = app?.user_profile?.firstName || '';
        const lastName = app?.user_profile?.lastName || '';
        const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'Borrower';
        const displayId = app?.application_details?.displayId || appId;
        return {
          value: appId,
          label: `${fullName} (${displayId})`,
          application_id: appId,
        };
      })
      .filter(Boolean);
  }, [pipelineData]);
  const selectedApplicationOption = useMemo(() => (
    applicationOptions.find((option) => option.value === normalizedApplicationId) || null
  ), [applicationOptions, normalizedApplicationId]);
  const canPersistBorrowerProfile = Boolean(isConsumer && applicationId);
  const { data: profileBootstrap } = useGetProfileBootstrapQuery(
    { applicationId },
    { skip: !canPersistBorrowerProfile, refetchOnMountOrArgChange: true }
  );
  const activeBorrowerId =
    profileBootstrap?.data?.current_user_borrower_id ||
    profileBootstrap?.data?.borrower_profile?.borrower_id ||
    profileBootstrap?.data?.borrowers?.find((borrower) => borrower?.role === 'primary')?.id ||
    null;
  const { data: borrowerScopedProfile } = useGetBorrowerScopedProfileQuery(
    { borrowerId: activeBorrowerId, section: 'personal_details', applicationId },
    { skip: !activeBorrowerId }
  );
  const existingPersonalDetails = borrowerScopedProfile?.data?.personal_details || {};
  const [updateBorrowerScopedProfile, { isLoading: isSavingBorrowerProfile }] =
    useUpdateBorrowerScopedProfileMutation();
  const [uploadConsumerProfilePhoto, { isLoading: isUploadingConsumerPhoto }] =
    useUploadConsumerProfilePhotoMutation();
  const [uploadUserProfilePhoto, { isLoading: isUploadingUserPhoto }] =
    useUploadUserProfilePhotoMutation();
  const avatarUrl =
    localImageUrl ||
    user?.avatar_url ||
    user?.profile_picture ||
    user?.profile_picture_url ||
    '';
  const seedFirstName = isConsumer
    ? existingPersonalDetails?.firstName || existingPersonalDetails?.first_name || ''
    : user?.first_name || user?.firstName || '';
  const seedLastName = isConsumer
    ? existingPersonalDetails?.lastName || existingPersonalDetails?.last_name || ''
    : user?.last_name || user?.lastName || '';
  const seedPhoneNumber = isConsumer
    ? existingPersonalDetails?.phoneNumber ||
      existingPersonalDetails?.phone_number ||
      existingPersonalDetails?.phone ||
      ''
    : user?.phone_number || user?.phoneNumber || user?.phone || '';
  const quickFormSeed = useMemo(() => ({
    firstName: seedFirstName,
    lastName: seedLastName,
    phoneNumber: seedPhoneNumber,
  }), [seedFirstName, seedLastName, seedPhoneNumber]);
  const workspaceSummary = useMemo(
    () => getWorkspaceSummary({
      pathname: location.pathname,
      search: location.search,
      tenant,
      branch,
      roleDescription: roleMetadata?.description || '',
    }),
    [location.pathname, location.search, tenant, branch, roleMetadata?.description]
  );
  const b2bActionItems = useMemo(
    () => getB2BActionItems({
      normalizedRole,
      navigate,
      onNotifications,
      onSupport,
    }),
    [normalizedRole, navigate, onNotifications, onSupport]
  );
  const b2bSupportItems = useMemo(
    () => b2bActionItems.filter((item) => item.key === 'notifications' || item.key === 'support'),
    [b2bActionItems]
  );
  const b2bWorkspaceItems = useMemo(
    () => b2bActionItems.filter((item) => item.key !== 'notifications' && item.key !== 'support'),
    [b2bActionItems]
  );
  const b2bPrimaryItems = useMemo(
    () => PRIMARY_B2B_ACTION_KEYS
      .map((key) => b2bWorkspaceItems.find((item) => item.key === key))
      .filter(Boolean),
    [b2bWorkspaceItems]
  );
  const b2bAdminItems = useMemo(
    () => ADMIN_B2B_ACTION_KEYS
      .map((key) => b2bWorkspaceItems.find((item) => item.key === key))
      .filter(Boolean),
    [b2bWorkspaceItems]
  );
  const b2bMobileItems = useMemo(
    () => [...b2bPrimaryItems.slice(0, 3), ...b2bAdminItems.slice(0, 2)],
    [b2bPrimaryItems, b2bAdminItems]
  );

  useEffect(() => {
    setQuickForm((prev) => {
      if (
        prev.firstName === seedFirstName &&
        prev.lastName === seedLastName &&
        prev.phoneNumber === seedPhoneNumber
      ) {
        return prev;
      }

      return {
        firstName: seedFirstName,
        lastName: seedLastName,
        phoneNumber: seedPhoneNumber,
      };
    });
  }, [
    normalizedApplicationId,
    seedFirstName,
    seedLastName,
    seedPhoneNumber,
  ]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    void (async () => {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;

      const previousLocalImageUrl = localImageUrl;
      const previewUrl = URL.createObjectURL(file);
      setLocalImageUrl(previewUrl);

      try {
        const userId = getUserId(user);
        let uploadResponse;

        if (isConsumer) {
          uploadResponse = await uploadConsumerProfilePhoto({ file }).unwrap();
        } else {
          if (!userId) {
            throw new Error('Missing user id for profile photo upload');
          }
          uploadResponse = await uploadUserProfilePhoto({ userId, file }).unwrap();
        }

        const uploadedPhotoUrl = getPhotoUrlFromResponse(uploadResponse);
        if (!uploadedPhotoUrl) {
          toast({
            title: 'Photo uploaded, but URL was not returned.',
            description: 'Please refresh to view the latest profile photo.',
          });
          return;
        }

        const updatedUser = {
          ...user,
          profile_picture_url: uploadedPhotoUrl,
          profilePictureUrl: uploadedPhotoUrl,
          profile_picture: uploadedPhotoUrl,
          avatar_url: uploadedPhotoUrl,
        };

        dispatch(setUser(updatedUser));
        tokenManager.setUserData(updatedUser);

        if (previousLocalImageUrl) {
          URL.revokeObjectURL(previousLocalImageUrl);
        }
        setLocalImageUrl('');
        toast({ title: 'Profile photo updated.' });
      } catch (error) {
        if (previousLocalImageUrl) {
          URL.revokeObjectURL(previousLocalImageUrl);
        }
        URL.revokeObjectURL(previewUrl);
        setLocalImageUrl('');

        const message = getReadableErrorMessage(error, 'Unable to update profile photo.');
        toast({
          title: 'Photo upload failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        if (event?.target) {
          event.target.value = '';
        }
      }
    })();
  };

  const handleFieldChange = (field) => (event) => {
    let nextValue = event.target.value || '';
    if (field === 'phoneNumber') {
      nextValue = nextValue.replace(/[^\d+()\-\s]/g, '');
    }
    setQuickForm((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleQuickSave = () => {
    if (isSavingBorrowerProfile) return;
    void (async () => {
    const nextFirstName = quickForm.firstName.trim();
    const nextLastName = quickForm.lastName.trim();
    const nextPhoneNumber = quickForm.phoneNumber.trim();

    if (!nextFirstName || !nextLastName) {
      toast({
        title: 'First and last name are required.',
        variant: 'destructive',
      });
      return;
    }

    if (canPersistBorrowerProfile && !activeBorrowerId) {
      toast({
        title: 'Unable to find active borrower profile.',
        description: 'Please refresh and try again.',
        variant: 'destructive',
      });
      return;
    }

    if (canPersistBorrowerProfile && activeBorrowerId) {
      try {
        await updateBorrowerScopedProfile({
          borrowerId: activeBorrowerId,
          section: 'personal_details',
          applicationId,
          data: {
            ...existingPersonalDetails,
            firstName: nextFirstName,
            lastName: nextLastName,
            phoneNumber: nextPhoneNumber,
          },
        }).unwrap();

        dispatch(
          applicationAPI.util.invalidateTags([
            { type: 'BorrowerScopedProfile', id: `${activeBorrowerId}-personal_details` },
            'BorrowerProfile',
            'ProfileBootstrap',
          ])
        );
      } catch (error) {
        const message = getReadableErrorMessage(
          error,
          'Unable to update profile details right now.'
        );
        toast({
          title: 'Save failed',
          description: message,
          variant: 'destructive',
        });
        return;
      }
    }

    const updatedUser = {
      ...user,
      first_name: nextFirstName,
      firstName: nextFirstName,
      last_name: nextLastName,
      lastName: nextLastName,
      phone_number: nextPhoneNumber,
      phoneNumber: nextPhoneNumber,
      phone: nextPhoneNumber,
      name: `${nextFirstName} ${nextLastName}`.trim(),
    };

    dispatch(setUser(updatedUser));
    tokenManager.setUserData(updatedUser);
    setShowQuickEdit(false);
    toast({ title: 'Account details updated.' });
    })();
  };

  const handleQuickCancel = () => {
    setQuickForm(quickFormSeed);
    setShowQuickEdit(false);
  };

  const handleApplicationChange = (option) => {
    const nextApplicationId = normalizeApplicationId(option?.value);
    if (!nextApplicationId || nextApplicationId === normalizedApplicationId) return;

    sessionStorage.setItem('b2c_active_application_id', nextApplicationId);
    localStorage.setItem('b2c_active_application_id', nextApplicationId);

    const params = new URLSearchParams(location.search || '');
    params.set('applicationId', nextApplicationId);
    params.delete('application_id');
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: false }
    );
  };

  return (
    <div
      className={`${styles.panel} ${mode === 'mobile' ? styles.mobile : ''} ${
        isDesktopB2BModal ? styles.panelModal : ''
      }`}
    >
      <div className={styles.headerRow}>
        <div>
          <p className={styles.headerCopy}>Account</p>
          {!isDesktopB2BModal ? (
            <p className={styles.headerDescription}>
              Review your workspace context, shortcuts, and basic account details.
            </p>
          ) : null}
        </div>
        {onClose ? (
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close account panel">
            <X size={14} />
          </button>
        ) : null}
      </div>

      <div className={styles.userCard}>
        <div className={styles.avatarWrap}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className={styles.avatarImage} />
          ) : (
            getInitials(displayName)
          )}
        </div>

        <div className={styles.userMeta}>
          <div className={styles.identityHeader}>
            <div className={styles.identityCopy}>
              {displayName ? <p className={styles.name}>{displayName}</p> : null}
              {user?.email ? <p className={styles.email}>{user.email}</p> : null}
            </div>

            <button
              type="button"
              className={styles.avatarAction}
              onClick={handleUploadClick}
              disabled={isUploadingConsumerPhoto || isUploadingUserPhoto}
            >
              <Camera size={13} />
              {isUploadingConsumerPhoto || isUploadingUserPhoto ? 'Uploading...' : 'Change Photo'}
            </button>
          </div>

          {!isConsumer && roleMetadata?.description ? (
            <p className={styles.roleDescription}>{roleMetadata.description}</p>
          ) : null}

          <div className={styles.chips}>
            {role ? <span className={styles.chip}>{role}</span> : null}
            {tenant ? <span className={styles.chip}>Tenant: {tenant}</span> : null}
            {branch ? <span className={styles.chip}>Branch: {branch}</span> : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {isConsumer ? (
        <>
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Current Application</p>
            <div className={styles.applicationCard}>
              <p className={styles.applicationName}>Active application</p>
              <Select
                options={applicationOptions}
                value={selectedApplicationOption}
                onChange={handleApplicationChange}
                placeholder="Select application"
                classNamePrefix="application-quick-select"
                styles={{
                  ...applicationSelectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 120 }),
                }}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                isSearchable={false}
              />
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.actionList}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => setShowQuickEdit((prev) => !prev)}
              >
                <span className={styles.actionLeft}>
                  <UserRound className={styles.actionIcon} />
                  <span className={styles.actionText}>My Profile</span>
                </span>
                <ChevronRight className={`${styles.actionArrow} ${showQuickEdit ? styles.actionArrowOpen : ''}`} />
              </button>

              {showQuickEdit ? (
                <div className={styles.quickEditCard}>
                  <div className={styles.quickEditGrid}>
                    <label className={styles.quickField}>
                      <span>First Name</span>
                      <input
                        type="text"
                        value={quickForm.firstName}
                        onChange={handleFieldChange('firstName')}
                        maxLength={60}
                      />
                    </label>
                    <label className={styles.quickField}>
                      <span>Last Name</span>
                      <input
                        type="text"
                        value={quickForm.lastName}
                        onChange={handleFieldChange('lastName')}
                        maxLength={60}
                      />
                    </label>
                    <label className={styles.quickField}>
                      <span>Phone Number</span>
                      <input
                        type="text"
                        value={quickForm.phoneNumber}
                        onChange={handleFieldChange('phoneNumber')}
                        maxLength={20}
                      />
                    </label>
                    <label className={styles.quickField}>
                      <span>Email</span>
                      <input
                        type="text"
                        value={user?.email || ''}
                        readOnly
                        disabled
                      />
                    </label>
                  </div>

                  <div className={styles.quickEditActions}>
                    <button type="button" className={styles.quickGhostBtn} onClick={handleQuickCancel}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.quickSaveBtn}
                      onClick={handleQuickSave}
                      disabled={isSavingBorrowerProfile}
                    >
                      <Save size={13} />
                      {isSavingBorrowerProfile ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : null}

              <button type="button" className={styles.actionBtn} onClick={onNotifications}>
                <span className={styles.actionLeft}>
                  <Bell className={styles.actionIcon} />
                  <span className={styles.actionText}>Notifications</span>
                </span>
                <ChevronRight className={styles.actionArrow} />
              </button>

              <button type="button" className={styles.actionBtn} onClick={onSupport}>
                <span className={styles.actionLeft}>
                  <CircleHelp className={styles.actionIcon} />
                  <span className={styles.actionText}>Help &amp; Support</span>
                </span>
                <ChevronRight className={styles.actionArrow} />
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={isDesktopB2BModal ? styles.b2bModalLayout : ''}>
            <div className={isDesktopB2BModal ? styles.b2bSidebar : ''}>
              <div className={styles.section}>
                <p className={styles.sectionLabel}>Quick Actions</p>
                <div className={styles.actionList}>
                  <button
                    type="button"
                    className={styles.actionDetailBtn}
                    onClick={() => setShowQuickEdit((prev) => !prev)}
                  >
                    <span className={styles.actionLeft}>
                      <span className={styles.actionGridIconWrap}>
                        <Settings2 className={styles.actionGridIcon} />
                      </span>
                      <span className={styles.actionDetailBody}>
                        <span className={styles.actionGridTitle}>My Profile</span>
                        <span className={styles.actionGridDescription}>Update your basic account details.</span>
                      </span>
                    </span>
                    <ChevronRight className={`${styles.actionArrow} ${showQuickEdit ? styles.actionArrowOpen : ''}`} />
                  </button>

                  {canManageRoles ? (
                    <button
                      type="button"
                      className={styles.actionDetailBtn}
                      onClick={() => setShowRoles((prev) => !prev)}
                      aria-pressed={showRoles}
                    >
                      <span className={styles.actionLeft}>
                        <span className={styles.actionGridIconWrap}>
                          <ShieldCheck className={styles.actionGridIcon} />
                        </span>
                        <span className={styles.actionDetailBody}>
                          <span className={styles.actionGridTitle}>Roles &amp; Access</span>
                          <span className={styles.actionGridDescription}>
                            Set what each role can do in a borrower file.
                          </span>
                        </span>
                      </span>
                      <ChevronRight className={`${styles.actionArrow} ${showRoles ? styles.actionArrowOpen : ''}`} />
                    </button>
                  ) : null}

                  {b2bSupportItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        type="button"
                        key={item.key}
                        className={styles.actionDetailBtn}
                        onClick={item.onClick}
                      >
                        <span className={styles.actionLeft}>
                          <span className={styles.actionGridIconWrap}>
                            <Icon className={styles.actionGridIcon} />
                          </span>
                          <span className={styles.actionDetailBody}>
                            <span className={styles.actionGridTitle}>{item.label}</span>
                            <span className={styles.actionGridDescription}>{item.description}</span>
                          </span>
                        </span>
                        <ChevronRight className={styles.actionArrow} />
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionLabel}>{workspaceSummary.eyebrow}</p>
                <div className={styles.workspaceCard}>
                  <div className={styles.workspaceHeader}>
                    <div>
                      <p className={styles.workspaceTitle}>{workspaceSummary.title}</p>
                      <p className={styles.workspaceDescription}>{workspaceSummary.description}</p>
                    </div>
                    <span className={styles.workspaceBadge}>
                      <BriefcaseBusiness size={12} />
                      B2B
                    </span>
                  </div>

                  {workspaceSummary.details.length ? (
                    <div className={styles.workspaceMeta}>
                      {workspaceSummary.details.map((detail) => (
                        <span key={detail} className={styles.workspaceMetaItem}>
                          {detail}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>

              {showQuickEdit ? (
                <div className={styles.section}>
                  <div className={styles.quickEditCard}>
                    <div className={styles.quickEditHeader}>
                      <p className={styles.quickEditTitle}>Edit Basic Details</p>
                      <p className={styles.quickEditDescription}>
                        Keep your account information current for team visibility and notifications.
                      </p>
                    </div>
                    <div className={styles.quickEditGrid}>
                      <label className={styles.quickField}>
                        <span>First Name</span>
                        <input
                          type="text"
                          value={quickForm.firstName}
                          onChange={handleFieldChange('firstName')}
                          maxLength={60}
                        />
                      </label>
                      <label className={styles.quickField}>
                        <span>Last Name</span>
                        <input
                          type="text"
                          value={quickForm.lastName}
                          onChange={handleFieldChange('lastName')}
                          maxLength={60}
                        />
                      </label>
                      <label className={styles.quickField}>
                        <span>Phone Number</span>
                        <input
                          type="text"
                          value={quickForm.phoneNumber}
                          onChange={handleFieldChange('phoneNumber')}
                          maxLength={20}
                        />
                      </label>
                      <label className={styles.quickField}>
                        <span>Email</span>
                        <input
                          type="text"
                          value={user?.email || ''}
                          readOnly
                          disabled
                        />
                      </label>
                    </div>

                    <div className={styles.quickEditActions}>
                      <button type="button" className={styles.quickGhostBtn} onClick={handleQuickCancel}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={styles.quickSaveBtn}
                        onClick={handleQuickSave}
                        disabled={isSavingBorrowerProfile}
                      >
                        <Save size={13} />
                        {isSavingBorrowerProfile ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className={isDesktopB2BModal ? styles.b2bMain : ''}>
              {showRoles && canManageRoles ? (
                <div className={styles.section}>
                  <div className={styles.mainSectionCard}>
                    <RolesAccessPanel />
                  </div>
                </div>
              ) : (
              <div className={styles.section}>
                <div className={styles.mainSectionCard}>
                  <div className={styles.workAreaHeader}>
                    <div>
                      <p className={styles.sectionLabel}>Work Areas</p>
                      <p className={styles.workAreaTitle}>
                        {isMobileB2B ? 'Go to a work area' : 'Choose where you want to work next'}
                      </p>
                      <p className={styles.workAreaDescription}>
                        {isMobileB2B
                          ? 'Use the most important destinations for day-to-day B2B work.'
                          : 'Open the part of the platform you use most often for operations, reporting, and borrower management.'}
                      </p>
                    </div>
                  </div>

                  {isMobileB2B ? (
                    <div className={styles.mobileWorkAreaList}>
                      {b2bMobileItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            type="button"
                            key={item.key}
                            className={styles.actionDetailBtn}
                            onClick={item.onClick}
                          >
                            <span className={styles.actionLeft}>
                              <span className={styles.actionGridIconWrap}>
                                <Icon className={styles.actionGridIcon} />
                              </span>
                              <span className={styles.actionDetailBody}>
                                <span className={styles.actionGridTitle}>{item.label}</span>
                                <span className={styles.actionGridDescription}>{item.description}</span>
                              </span>
                            </span>
                            <ChevronRight className={styles.actionArrow} />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <>
                      <div className={styles.workAreaSection}>
                        <div className={styles.workAreaSectionHeader}>
                          <p className={styles.workAreaSectionTitle}>Primary Work</p>
                          <p className={styles.workAreaSectionDescription}>
                            The areas you are most likely to use during day-to-day operations.
                          </p>
                        </div>

                        <div className={isDesktopB2BModal ? styles.workAreaGrid : styles.actionList}>
                          {b2bPrimaryItems.map((item) => {
                            const Icon = item.icon;
                            return (
                              <button
                                type="button"
                                key={item.key}
                                className={isDesktopB2BModal ? styles.workAreaCard : styles.actionDetailBtn}
                                onClick={item.onClick}
                              >
                                <span className={styles.actionLeft}>
                                  <span className={styles.actionGridIconWrap}>
                                    <Icon className={styles.actionGridIcon} />
                                  </span>
                                  <span className={styles.actionDetailBody}>
                                    <span className={styles.actionGridTitle}>{item.label}</span>
                                    <span className={styles.actionGridDescription}>{item.description}</span>
                                  </span>
                                </span>
                                <ChevronRight className={styles.actionArrow} />
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {b2bAdminItems.length ? (
                        <div className={styles.workAreaSection}>
                          <div className={styles.workAreaSectionHeader}>
                            <p className={styles.workAreaSectionTitle}>Administration</p>
                            <p className={styles.workAreaSectionDescription}>
                              Management tools for employees, consumers, branches, reporting, and invites.
                            </p>
                          </div>

                          <div className={styles.adminGrid}>
                            {b2bAdminItems.map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  type="button"
                                  key={item.key}
                                  className={styles.adminAreaCard}
                                  onClick={item.onClick}
                                >
                                  <span className={styles.actionLeft}>
                                    <span className={styles.actionGridIconWrap}>
                                      <Icon className={styles.actionGridIcon} />
                                    </span>
                                    <span className={styles.actionDetailBody}>
                                      <span className={styles.actionGridTitle}>{item.label}</span>
                                      <span className={styles.actionGridDescription}>{item.description}</span>
                                    </span>
                                  </span>
                                  <ChevronRight className={styles.actionArrow} />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </>
                  )}

                  {!b2bPrimaryItems.length && !b2bAdminItems.length ? (
                    <div className={isDesktopB2BModal ? styles.workAreaGrid : styles.actionList}>
                      {b2bWorkspaceItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          type="button"
                          key={item.key}
                          className={isDesktopB2BModal ? styles.workAreaCard : styles.actionDetailBtn}
                          onClick={item.onClick}
                        >
                          <span className={styles.actionLeft}>
                            <span className={styles.actionGridIconWrap}>
                              <Icon className={styles.actionGridIcon} />
                            </span>
                            <span className={styles.actionDetailBody}>
                              <span className={styles.actionGridTitle}>{item.label}</span>
                              <span className={styles.actionGridDescription}>{item.description}</span>
                            </span>
                          </span>
                          <ChevronRight className={styles.actionArrow} />
                        </button>
                      );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountQuickPanel;
